import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Collapse,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Tabs,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  createAmarkosh,
  deleteAmarkosh,
  getAmarkosh,
  listAmarkosh,
  updateAmarkosh,
  type Amarkosh,
  type AmarkoshWordSynonymWithSubanta,
  type AmarkoshWordWithSynonyms,
  type AmarkoshWithWords,
} from '../api/amarkoshApi';
import {
  createAmarkoshWord,
  deleteAmarkoshWord,
  getAmarkoshWord,
  listAmarkoshWords,
  updateAmarkoshWord,
  type AmarkoshWord,
} from '../api/amarkoshWordApi';
import {
  createAmarkoshWordSynonym,
  deleteAmarkoshWordSynonym,
  getAmarkoshWordSynonym,
  listAmarkoshWordSynonyms,
  updateAmarkoshWordSynonym,
  type AmarkoshWordSynonym,
} from '../api/amarkoshWordSynonymApi';
import { listSubantas, type Subanta } from '../api/subantaApi';
import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';

const apiBase =
  process.env.REACT_APP_DICTIONARY_API_URL || 'https://koshaapidev.vag.org.np';

function apiErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return ax?.response?.data?.message || ax?.message || fallback;
}

function buildAmarkoshPatch(
  prev: Amarkosh,
  next: { num: string; text: string }
): Partial<{ num: string; text: string }> {
  const patch: Partial<{ num: string; text: string }> = {};
  if (prev.num !== next.num) patch.num = next.num;
  if (prev.text !== next.text) patch.text = next.text;
  return patch;
}

function buildAmarkoshWordPatch(
  prev: AmarkoshWord,
  next: { word: string; linga: string; artha: string; amarkoshId: number }
): Partial<Pick<AmarkoshWord, 'word' | 'linga' | 'artha' | 'amarkoshId'>> {
  const patch: Partial<Pick<AmarkoshWord, 'word' | 'linga' | 'artha' | 'amarkoshId'>> = {};
  if (prev.word !== next.word) patch.word = next.word;
  if (prev.linga !== next.linga) patch.linga = next.linga;
  if (prev.artha !== next.artha) patch.artha = next.artha;
  if (prev.amarkoshId !== next.amarkoshId) patch.amarkoshId = next.amarkoshId;
  return patch;
}

function buildSynonymPatch(
  prev: AmarkoshWordSynonym,
  next: { amarkoshWordId: number; subantaId: number }
): Partial<Pick<AmarkoshWordSynonym, 'amarkoshWordId' | 'subantaId'>> {
  const patch: Partial<Pick<AmarkoshWordSynonym, 'amarkoshWordId' | 'subantaId'>> = {};
  if (prev.amarkoshWordId !== next.amarkoshWordId) patch.amarkoshWordId = next.amarkoshWordId;
  if (prev.subantaId !== next.subantaId) patch.subantaId = next.subantaId;
  return patch;
}

export function AmarkoshaPage() {
  const [amarkoshRows, setAmarkoshRows] = useState<Amarkosh[]>([]);
  const [wordRows, setWordRows] = useState<AmarkoshWord[]>([]);
  const [synonymRows, setSynonymRows] = useState<AmarkoshWordSynonym[]>([]);
  const [subantaRows, setSubantaRows] = useState<Subanta[]>([]);

  const [amarkoshLoading, setAmarkoshLoading] = useState(false);
  const [wordLoading, setWordLoading] = useState(false);
  const [synonymLoading, setSynonymLoading] = useState(false);
  const [subantaLoading, setSubantaLoading] = useState(false);

  const [amarkoshModalOpen, setAmarkoshModalOpen] = useState(false);
  const [amarkoshEditing, setAmarkoshEditing] = useState<Amarkosh | null>(null);
  const [amarkoshSaving, setAmarkoshSaving] = useState(false);
  const [amarkoshDetailOpen, setAmarkoshDetailOpen] = useState(false);
  const [amarkoshDetailLoading, setAmarkoshDetailLoading] = useState(false);
  const [amarkoshDetail, setAmarkoshDetail] = useState<AmarkoshWithWords | null>(null);
  const [amarkoshDetailExpandedWordKeys, setAmarkoshDetailExpandedWordKeys] = useState<string[]>([]);
  const [amarkoshForm] = Form.useForm<{ num: string; text: string }>();

  const [wordModalOpen, setWordModalOpen] = useState(false);
  const [wordEditing, setWordEditing] = useState<AmarkoshWord | null>(null);
  const [wordSaving, setWordSaving] = useState(false);
  const [wordDetailOpen, setWordDetailOpen] = useState(false);
  const [wordDetailLoading, setWordDetailLoading] = useState(false);
  const [wordDetail, setWordDetail] = useState<AmarkoshWord | null>(null);
  const [wordForm] = Form.useForm<{
    word: string;
    linga: string;
    artha: string;
    amarkoshId: number;
  }>();

  const [synModalOpen, setSynModalOpen] = useState(false);
  const [synEditing, setSynEditing] = useState<AmarkoshWordSynonym | null>(null);
  const [synSaving, setSynSaving] = useState(false);
  const [synDetailOpen, setSynDetailOpen] = useState(false);
  const [synDetailLoading, setSynDetailLoading] = useState(false);
  const [synDetail, setSynDetail] = useState<AmarkoshWordSynonym | null>(null);
  const [synForm] = Form.useForm<{ amarkoshWordId: number; subantaId: number }>();

  /** When set, word modal fixes amarkosh (opened from entry detail). */
  const [wordModalLockedAmarkoshId, setWordModalLockedAmarkoshId] = useState<number | null>(null);
  /** When set, synonym modal fixes amarkosh word (opened from entry detail). */
  const [synModalLockedWordId, setSynModalLockedWordId] = useState<number | null>(null);

  const amarkoshDetailRef = React.useRef<AmarkoshWithWords | null>(null);
  amarkoshDetailRef.current = amarkoshDetail;

  const bumpOpenAmarkoshDetail = useCallback(async () => {
    if (!amarkoshDetailOpen) return;
    const cur = amarkoshDetailRef.current;
    if (!cur) return;
    try {
      setAmarkoshDetail(await getAmarkosh(cur.id));
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to refresh entry'));
    }
  }, [amarkoshDetailOpen]);

  const loadAmarkosh = useCallback(async () => {
    setAmarkoshLoading(true);
    try {
      setAmarkoshRows(await listAmarkosh());
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load amarkosh'));
    } finally {
      setAmarkoshLoading(false);
    }
  }, []);

  const loadWords = useCallback(async () => {
    setWordLoading(true);
    try {
      setWordRows(await listAmarkoshWords());
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load amarkosh-word'));
    } finally {
      setWordLoading(false);
    }
  }, []);

  const loadSynonyms = useCallback(async () => {
    setSynonymLoading(true);
    try {
      setSynonymRows(await listAmarkoshWordSynonyms());
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load amarkosh-word-synonym'));
    } finally {
      setSynonymLoading(false);
    }
  }, []);

  const loadSubantas = useCallback(async () => {
    setSubantaLoading(true);
    try {
      setSubantaRows(await listSubantas());
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load subantas'));
    } finally {
      setSubantaLoading(false);
    }
  }, []);

  useEffect(() => {
    void Promise.all([loadAmarkosh(), loadWords(), loadSynonyms(), loadSubantas()]);
  }, [loadAmarkosh, loadWords, loadSynonyms, loadSubantas]);

  const amarkoshOptions = useMemo(
    () =>
      [...amarkoshRows]
        .sort((a, b) => String(a.num).localeCompare(String(b.num), undefined, { numeric: true }))
        .map((a) => ({
          value: a.id,
          label: `${a.num} (#${a.id})`,
        })),
    [amarkoshRows]
  );

  const wordOptions = useMemo(
    () =>
      [...wordRows]
        .sort((a, b) => a.id - b.id)
        .map((w) => ({
          value: w.id,
          label: `${w.word} · ${w.linga} (#${w.id})`,
        })),
    [wordRows]
  );

  const subantaOptions = useMemo(
    () =>
      [...subantaRows]
        .sort((a, b) =>
          String(a.word ?? '').localeCompare(String(b.word ?? ''), undefined, {
            numeric: true,
            sensitivity: 'base',
          })
        )
        .map((s) => ({
          value: s.id,
          label: `${s.word} (#${s.id}) · ${s.iast}`,
        })),
    [subantaRows]
  );

  const amarkoshLabel = useCallback(
    (id: number) => {
      const a = amarkoshRows.find((x) => x.id === id);
      return a ? `${a.num} (#${id})` : `#${id}`;
    },
    [amarkoshRows]
  );

  const wordShortLabel = useCallback(
    (id: number) => {
      const w = wordRows.find((x) => x.id === id);
      return w ? `${w.word} (#${id})` : `#${id}`;
    },
    [wordRows]
  );

  /* ——— Amarkosh CRUD ——— */
  const openAmarkoshCreate = () => {
    setAmarkoshEditing(null);
    amarkoshForm.resetFields();
    setAmarkoshModalOpen(true);
  };

  const openAmarkoshEdit = (r: Amarkosh) => {
    setAmarkoshEditing(r);
    amarkoshForm.setFieldsValue({ num: r.num, text: r.text });
    setAmarkoshModalOpen(true);
  };

  const closeAmarkoshModal = () => {
    setAmarkoshModalOpen(false);
    setAmarkoshEditing(null);
    amarkoshForm.resetFields();
  };

  const submitAmarkosh = async () => {
    try {
      const values = await amarkoshForm.validateFields();
      setAmarkoshSaving(true);
      const body = { num: values.num.trim(), text: values.text.trim() };
      if (amarkoshEditing) {
        const patch = buildAmarkoshPatch(amarkoshEditing, body);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeAmarkoshModal();
          return;
        }
        await updateAmarkosh(amarkoshEditing.id, patch);
        message.success('Amarkosh updated');
      } else {
        await createAmarkosh(body);
        message.success('Amarkosh created');
      }
      closeAmarkoshModal();
      await loadAmarkosh();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setAmarkoshSaving(false);
    }
  };

  const openAmarkoshDetail = async (id: number) => {
    setAmarkoshDetailOpen(true);
    setAmarkoshDetail(null);
    setAmarkoshDetailExpandedWordKeys([]);
    setAmarkoshDetailLoading(true);
    try {
      setAmarkoshDetail(await getAmarkosh(id));
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load amarkosh'));
      setAmarkoshDetailOpen(false);
    } finally {
      setAmarkoshDetailLoading(false);
    }
  };

  const deleteAmarkoshRow = async (id: number) => {
    try {
      await deleteAmarkosh(id);
      message.success('Amarkosh deleted');
      await loadAmarkosh();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Delete failed'));
    }
  };

  const amarkoshColumns: ColumnsType<Amarkosh> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Num', dataIndex: 'num', width: 100, ellipsis: true },
    { title: 'Text', dataIndex: 'text', ellipsis: true },
    {
      title: 'Actions',
      key: 'a',
      width: 220,
      render: (_, r) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openAmarkoshDetail(r.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openAmarkoshEdit(r)}>
            Edit
          </Button>
          <Popconfirm title="Delete this entry?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void deleteAmarkoshRow(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ——— Amarkosh-word CRUD ——— */
  const openWordCreate = () => {
    setWordEditing(null);
    setWordModalLockedAmarkoshId(null);
    wordForm.resetFields();
    const firstId = amarkoshRows[0]?.id;
    if (firstId != null) wordForm.setFieldsValue({ amarkoshId: firstId, word: '', linga: '', artha: '' });
    setWordModalOpen(true);
  };

  const openWordCreateForDetail = (amarkoshId: number) => {
    setWordEditing(null);
    setWordModalLockedAmarkoshId(amarkoshId);
    wordForm.resetFields();
    wordForm.setFieldsValue({ word: '', linga: '', artha: '' });
    setWordModalOpen(true);
  };

  const openWordEdit = (r: AmarkoshWord) => {
    setWordEditing(r);
    setWordModalLockedAmarkoshId(null);
    wordForm.setFieldsValue({
      word: r.word,
      linga: r.linga,
      artha: r.artha,
      amarkoshId: r.amarkoshId,
    });
    setWordModalOpen(true);
  };

  const openWordEditFromDetail = (r: AmarkoshWordWithSynonyms) => {
    setWordEditing(r);
    setWordModalLockedAmarkoshId(r.amarkoshId);
    wordForm.setFieldsValue({
      word: r.word,
      linga: r.linga,
      artha: r.artha,
    });
    setWordModalOpen(true);
  };

  const closeWordModal = () => {
    setWordModalOpen(false);
    setWordEditing(null);
    setWordModalLockedAmarkoshId(null);
    wordForm.resetFields();
  };

  const submitWord = async () => {
    try {
      const values = await wordForm.validateFields();
      const amarkoshId = wordModalLockedAmarkoshId ?? values.amarkoshId;
      if (amarkoshId == null || Number.isNaN(Number(amarkoshId))) {
        message.error('Choose an amarkosh entry');
        return;
      }
      setWordSaving(true);
      const body = {
        word: values.word.trim(),
        linga: values.linga.trim(),
        artha: values.artha.trim(),
        amarkoshId: Number(amarkoshId),
      };
      if (wordEditing) {
        const patch = buildAmarkoshWordPatch(wordEditing, body);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeWordModal();
          return;
        }
        await updateAmarkoshWord(wordEditing.id, patch);
        message.success('Amarkosh word updated');
      } else {
        await createAmarkoshWord(body);
        message.success('Amarkosh word created');
      }
      closeWordModal();
      await loadWords();
      await bumpOpenAmarkoshDetail();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setWordSaving(false);
    }
  };

  const openWordDetail = async (id: number) => {
    setWordDetailOpen(true);
    setWordDetail(null);
    setWordDetailLoading(true);
    try {
      setWordDetail(await getAmarkoshWord(id));
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load word'));
      setWordDetailOpen(false);
    } finally {
      setWordDetailLoading(false);
    }
  };

  const deleteWordRow = async (id: number) => {
    try {
      await deleteAmarkoshWord(id);
      message.success('Word deleted');
      await loadWords();
      await bumpOpenAmarkoshDetail();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Delete failed'));
    }
  };

  const wordColumns: ColumnsType<AmarkoshWord> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    { title: 'Word', dataIndex: 'word', width: 120, ellipsis: true },
    { title: 'Liṅga', dataIndex: 'linga', width: 72 },
    { title: 'Artha', dataIndex: 'artha', ellipsis: true },
    {
      title: 'Amarkosh',
      key: 'ak',
      width: 160,
      ellipsis: true,
      render: (_, r) => amarkoshLabel(r.amarkoshId),
    },
    {
      title: 'Actions',
      key: 'a',
      width: 220,
      render: (_, r) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openWordDetail(r.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openWordEdit(r)}>
            Edit
          </Button>
          <Popconfirm title="Delete this word?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void deleteWordRow(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ——— Synonym CRUD ——— */
  const openSynCreate = () => {
    setSynEditing(null);
    setSynModalLockedWordId(null);
    synForm.resetFields();
    const firstW = wordRows[0]?.id;
    if (firstW != null) synForm.setFieldsValue({ amarkoshWordId: firstW, subantaId: undefined });
    setSynModalOpen(true);
  };

  const openSynCreateForWord = (amarkoshWordId: number) => {
    setSynEditing(null);
    setSynModalLockedWordId(amarkoshWordId);
    synForm.resetFields();
    synForm.setFieldsValue({ subantaId: undefined });
    setSynModalOpen(true);
  };

  const openSynEdit = (r: AmarkoshWordSynonym) => {
    setSynEditing(r);
    setSynModalLockedWordId(null);
    synForm.setFieldsValue({ amarkoshWordId: r.amarkoshWordId, subantaId: r.subantaId });
    setSynModalOpen(true);
  };

  const openSynEditFromDetail = (r: AmarkoshWordSynonymWithSubanta) => {
    setSynEditing(r);
    setSynModalLockedWordId(r.amarkoshWordId);
    synForm.setFieldsValue({ subantaId: r.subantaId });
    setSynModalOpen(true);
  };

  const closeSynModal = () => {
    setSynModalOpen(false);
    setSynEditing(null);
    setSynModalLockedWordId(null);
    synForm.resetFields();
  };

  const submitSyn = async () => {
    try {
      const values = await synForm.validateFields();
      const amarkoshWordId = synModalLockedWordId ?? values.amarkoshWordId;
      if (amarkoshWordId == null || Number.isNaN(Number(amarkoshWordId))) {
        message.error('Choose an amarkosh word');
        return;
      }
      setSynSaving(true);
      const merged = { amarkoshWordId: Number(amarkoshWordId), subantaId: values.subantaId };
      if (synEditing) {
        const patch = buildSynonymPatch(synEditing, merged);
        if (Object.keys(patch).length === 0) {
          message.info('No changes to save');
          closeSynModal();
          return;
        }
        await updateAmarkoshWordSynonym(synEditing.id, patch);
        message.success('Synonym link updated');
      } else {
        await createAmarkoshWordSynonym({
          amarkoshWordId: merged.amarkoshWordId,
          subantaId: merged.subantaId,
        });
        message.success('Synonym link created');
      }
      closeSynModal();
      await loadSynonyms();
      await bumpOpenAmarkoshDetail();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(apiErrorMessage(err, 'Save failed'));
    } finally {
      setSynSaving(false);
    }
  };

  const openSynDetail = async (id: number) => {
    setSynDetailOpen(true);
    setSynDetail(null);
    setSynDetailLoading(true);
    try {
      setSynDetail(await getAmarkoshWordSynonym(id));
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Failed to load synonym'));
      setSynDetailOpen(false);
    } finally {
      setSynDetailLoading(false);
    }
  };

  const deleteSynRow = async (id: number) => {
    try {
      await deleteAmarkoshWordSynonym(id);
      message.success('Synonym deleted');
      await loadSynonyms();
      await bumpOpenAmarkoshDetail();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err, 'Delete failed'));
    }
  };

  const synColumns: ColumnsType<AmarkoshWordSynonym> = [
    { title: 'ID', dataIndex: 'id', width: 72 },
    {
      title: 'Amarkosh word',
      key: 'w',
      width: 200,
      ellipsis: true,
      render: (_, r) => wordShortLabel(r.amarkoshWordId),
    },
    { title: 'Subanta ID', dataIndex: 'subantaId', width: 110 },
    {
      title: 'Actions',
      key: 'a',
      width: 220,
      render: (_, r) => (
        <Space size="small" wrap>
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => void openSynDetail(r.id)}>
            View
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openSynEdit(r)}>
            Edit
          </Button>
          <Popconfirm title="Delete this synonym link?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void deleteSynRow(r.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const tabItems = [
    {
      key: 'amarkosh',
      label: 'Amarkosh',
      children: (
        <Card
          title="Entries (/amarkosh)"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openAmarkoshCreate}>
              Add
            </Button>
          }
        >
          <Table<Amarkosh>
            rowKey="id"
            loading={amarkoshLoading}
            columns={amarkoshColumns}
            dataSource={amarkoshRows}
            pagination={{ pageSize: 10, showSizeChanger: true }}
            scroll={{ x: true }}
          />
        </Card>
      ),
    },
    {
      key: 'word',
      label: 'Amarkosh word',
      children: (
        <Card
          title="Words (/amarkosh-word)"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openWordCreate} disabled={amarkoshRows.length === 0}>
              Add
            </Button>
          }
        >
          {amarkoshRows.length === 0 ? (
            <Typography.Text type="secondary">Create at least one Amarkosh entry first.</Typography.Text>
          ) : (
            <Table<AmarkoshWord>
              rowKey="id"
              loading={wordLoading}
              columns={wordColumns}
              dataSource={wordRows}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: true }}
            />
          )}
        </Card>
      ),
    },
    {
      key: 'synonym',
      label: 'Synonyms',
      children: (
        <Card
          title="Word ↔ subanta (/amarkosh-word-synonym)"
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={openSynCreate} disabled={wordRows.length === 0}>
              Add
            </Button>
          }
        >
          {wordRows.length === 0 ? (
            <Typography.Text type="secondary">Create at least one Amarkosh word first.</Typography.Text>
          ) : (
            <Table<AmarkoshWordSynonym>
              rowKey="id"
              loading={synonymLoading}
              columns={synColumns}
              dataSource={synonymRows}
              pagination={{ pageSize: 10, showSizeChanger: true }}
              scroll={{ x: true }}
            />
          )}
        </Card>
      ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Amarkosh
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        REST mirror of <code>/amarkosh</code>, <code>/amarkosh-word</code>, and{' '}
        <code>/amarkosh-word-synonym</code> on {apiBase}.
      </Typography.Paragraph>

      <Tabs items={tabItems} />

      {/* Amarkosh modals */}
      <Modal
        title={amarkoshDetail ? `Amarkosh #${amarkoshDetail.id}` : 'Amarkosh'}
        open={amarkoshDetailOpen}
        width={920}
        destroyOnClose
        styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
        onCancel={() => {
          setAmarkoshDetailOpen(false);
          setAmarkoshDetail(null);
          setAmarkoshDetailExpandedWordKeys([]);
        }}
        footer={null}
      >
        {amarkoshDetailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : amarkoshDetail ? (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space direction="vertical" size={4}>
              <Typography.Text>
                <strong>Num:</strong> {amarkoshDetail.num}
              </Typography.Text>
              <Typography.Paragraph style={{ marginBottom: 0 }}>
                <strong>Text:</strong> {amarkoshDetail.text}
              </Typography.Paragraph>
            </Space>
            <Divider style={{ margin: '8px 0' }} />
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Typography.Title level={5} style={{ margin: 0 }}>
                Words ({amarkoshDetail.words?.length ?? 0})
              </Typography.Title>
              <Button
                type="primary"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => openWordCreateForDetail(amarkoshDetail.id)}
              >
                Add word
              </Button>
            </div>
            {(amarkoshDetail.words?.length ?? 0) === 0 ? (
              <Typography.Text type="secondary">
                No words yet. Use <strong>Add word</strong> above.
              </Typography.Text>
            ) : (
              <Collapse
                bordered={false}
                destroyInactivePanel
                activeKey={amarkoshDetailExpandedWordKeys}
                onChange={(keys) => {
                  const normalized = Array.isArray(keys) ? keys.map((k) => String(k)) : [String(keys)];
                  setAmarkoshDetailExpandedWordKeys(normalized);
                }}
                items={(amarkoshDetail.words ?? []).map((w) => ({
                  key: String(w.id),
                  label: (
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 8,
                        width: '100%',
                        paddingRight: 8,
                      }}
                    >
                      <Space wrap size="small">
                        <Typography.Text strong>{w.word}</Typography.Text>
                        <Typography.Text type="secondary">
                          {w.linga} · #{w.id}
                        </Typography.Text>
                      </Space>
                      <Space size="small" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="small"
                          type="primary"
                          ghost
                          icon={<PlusOutlined />}
                          onClick={() => openSynCreateForWord(w.id)}
                        >
                          Synonym
                        </Button>
                        <Button
                          size="small"
                          icon={<EditOutlined />}
                          onClick={() => openWordEditFromDetail(w)}
                        >
                          Edit
                        </Button>
                        <Popconfirm
                          title="Delete this word and its synonym links?"
                          okText="Delete"
                          okButtonProps={{ danger: true }}
                          onConfirm={() => void deleteWordRow(w.id)}
                        >
                          <Button size="small" danger icon={<DeleteOutlined />}>
                            Delete
                          </Button>
                        </Popconfirm>
                      </Space>
                    </div>
                  ),
                  children: amarkoshDetailExpandedWordKeys.includes(String(w.id)) ? (
                    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                      <Typography.Paragraph style={{ marginBottom: 0 }}>
                        <strong>Artha:</strong> {w.artha}
                      </Typography.Paragraph>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        Synonyms (dictionary subanta){' '}
                        {(w.synonyms?.length ?? 0) === 0
                          ? '— none yet'
                          : `— ${w.synonyms?.length} linked`}
                      </Typography.Text>
                      <Table<AmarkoshWordSynonymWithSubanta>
                        size="small"
                        pagination={{ pageSize: 8, hideOnSinglePage: true }}
                        rowKey="id"
                        scroll={{ x: true }}
                        dataSource={w.synonyms ?? []}
                        locale={{ emptyText: 'No synonyms — use Synonym in the header above.' }}
                        expandable={{
                          columnWidth: 44,
                          rowExpandable: (syn) => syn.subanta != null,
                          expandIcon: ({ expanded, onExpand, record }) => (
                            <Button
                              size="small"
                              onClick={(event) => onExpand(record, event)}
                              aria-label={expanded ? 'Collapse arthas' : 'Expand arthas'}
                            >
                              {expanded ? '-' : '+'}
                            </Button>
                          ),
                          expandedRowRender: (syn) => {
                            const arthas = syn.subanta?.arthas;
                            if (!arthas?.length) {
                              return (
                                <Typography.Text type="secondary" style={{ padding: '4px 8px', display: 'block' }}>
                                  No arthas on this subanta.
                                </Typography.Text>
                              );
                            }
                            const byLanguage = new Map<
                              number,
                              { label: string; rows: NonNullable<typeof arthas> }
                            >();
                            for (const a of arthas) {
                              const lid = a.languageId;
                              const label = (a.language?.name ?? a.language?.code) || `Language #${lid}`;
                              const bucket = byLanguage.get(lid);
                              if (bucket) {
                                bucket.rows.push(a);
                              } else {
                                byLanguage.set(lid, { label, rows: [a] });
                              }
                            }
                            const tabItems = Array.from(byLanguage.entries())
                              .sort((x, y) => x[1].label.localeCompare(y[1].label, undefined, { sensitivity: 'base' }))
                              .map(([lid, group]) => ({
                                key: String(lid),
                                label: `${group.label} (${group.rows.length})`,
                                children: (
                                  <Space
                                    direction="vertical"
                                    size="small"
                                    style={{ width: '100%', padding: '4px 8px 12px' }}
                                  >
                                    {group.rows.map((a) => (
                                      <Typography.Paragraph
                                        key={a.id}
                                        style={{ margin: 0, whiteSpace: 'pre-wrap' }}
                                      >
                                        {a.text}
                                      </Typography.Paragraph>
                                    ))}
                                  </Space>
                                ),
                              }));
                            return (
                              <Space direction="vertical" size="small" style={{ width: '100%', padding: '4px 8px 12px' }}>
                                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                  Subanta arthas ({arthas.length})
                                </Typography.Text>
                                <Tabs size="small" items={tabItems} />
                              </Space>
                            );
                          },
                        }}
                        columns={[
                          { title: 'Link ID', dataIndex: 'id', width: 72 },
                          { title: 'Subanta ID', dataIndex: 'subantaId', width: 88 },
                          {
                            title: 'Subanta',
                            key: 'sw',
                            ellipsis: true,
                            render: (_, syn) => syn.subanta?.word ?? '—',
                          },
                          {
                            title: 'IAST',
                            key: 'iast',
                            width: 120,
                            ellipsis: true,
                            render: (_, syn) => syn.subanta?.iast ?? '—',
                          },
                          {
                            title: 'Liṅga',
                            key: 'lg',
                            width: 110,
                            ellipsis: true,
                            render: (_, syn) => syn.subanta?.linga ?? '—',
                          },
                          {
                            title: 'Actions',
                            key: 'act',
                            width: 168,
                            render: (_, syn) => (
                              <Space size="small" wrap>
                                <Button
                                  type="link"
                                  size="small"
                                  icon={<EditOutlined />}
                                  onClick={() => openSynEditFromDetail(syn)}
                                >
                                  Edit
                                </Button>
                                <Popconfirm
                                  title="Remove this synonym link?"
                                  okText="Remove"
                                  okButtonProps={{ danger: true }}
                                  onConfirm={() => void deleteSynRow(syn.id)}
                                >
                                  <Button type="link" size="small" danger icon={<DeleteOutlined />}>
                                    Delete
                                  </Button>
                                </Popconfirm>
                              </Space>
                            ),
                          },
                        ]}
                      />
                    </Space>
                  ) : null,
                }))}
              />
            )}
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={amarkoshEditing ? 'Edit amarkosh' : 'Add amarkosh'}
        open={amarkoshModalOpen}
        onCancel={closeAmarkoshModal}
        onOk={() => void submitAmarkosh()}
        confirmLoading={amarkoshSaving}
        destroyOnClose
      >
        <Form form={amarkoshForm} layout="vertical" preserve={false}>
          <Form.Item name="num" label="Num" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. 1.1.6" />
          </Form.Item>
          <Form.Item name="text" label="Text" rules={[{ required: true, message: 'Required' }]}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Word modals */}
      <Modal
        title={wordDetail ? `Word #${wordDetail.id}` : 'Word'}
        open={wordDetailOpen}
        onCancel={() => {
          setWordDetailOpen(false);
          setWordDetail(null);
        }}
        footer={null}
      >
        {wordDetailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : wordDetail ? (
          <Space direction="vertical">
            <Typography.Text>
              <strong>Word:</strong> {wordDetail.word}
            </Typography.Text>
            <Typography.Text>
              <strong>Liṅga:</strong> {wordDetail.linga}
            </Typography.Text>
            <Typography.Text>
              <strong>Artha:</strong> {wordDetail.artha}
            </Typography.Text>
            <Typography.Text>
              <strong>Amarkosh:</strong> {amarkoshLabel(wordDetail.amarkoshId)}
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={wordEditing ? 'Edit amarkosh word' : 'Add amarkosh word'}
        open={wordModalOpen}
        onCancel={closeWordModal}
        onOk={() => void submitWord()}
        confirmLoading={wordSaving}
        destroyOnClose
      >
        <Form form={wordForm} layout="vertical" preserve={false}>
          {wordModalLockedAmarkoshId != null ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Amarkosh entry:{' '}
              <strong>{amarkoshLabel(wordModalLockedAmarkoshId)}</strong>
            </Typography.Paragraph>
          ) : (
            <Form.Item name="amarkoshId" label="Amarkosh" rules={[{ required: true, message: 'Required' }]}>
              <Select options={amarkoshOptions} showSearch optionFilterProp="label" placeholder="Choose entry" />
            </Form.Item>
          )}
          <Form.Item name="word" label="Word" rules={[{ required: true, message: 'Required' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="linga" label="Liṅga" rules={[{ required: true, message: 'Required' }]}>
            <Input placeholder="e.g. A, P" />
          </Form.Item>
          <Form.Item name="artha" label="Artha" rules={[{ required: true, message: 'Required' }]}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Synonym modals */}
      <Modal
        title={synDetail ? `Synonym #${synDetail.id}` : 'Synonym'}
        open={synDetailOpen}
        onCancel={() => {
          setSynDetailOpen(false);
          setSynDetail(null);
        }}
        footer={null}
      >
        {synDetailLoading ? (
          <Typography.Text type="secondary">Loading…</Typography.Text>
        ) : synDetail ? (
          <Space direction="vertical">
            <Typography.Text>
              <strong>Amarkosh word:</strong> {wordShortLabel(synDetail.amarkoshWordId)}
            </Typography.Text>
            <Typography.Text>
              <strong>Subanta ID:</strong> {synDetail.subantaId}
            </Typography.Text>
          </Space>
        ) : null}
      </Modal>

      <Modal
        title={synEditing ? 'Edit synonym' : 'Add synonym'}
        open={synModalOpen}
        onCancel={closeSynModal}
        onOk={() => void submitSyn()}
        confirmLoading={synSaving}
        destroyOnClose
      >
        <Form form={synForm} layout="vertical" preserve={false}>
          {synModalLockedWordId != null ? (
            <Typography.Paragraph type="secondary" style={{ marginBottom: 16 }}>
              Amarkosh word: <strong>{wordShortLabel(synModalLockedWordId)}</strong>
            </Typography.Paragraph>
          ) : (
            <Form.Item
              name="amarkoshWordId"
              label="Amarkosh word"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Select options={wordOptions} showSearch optionFilterProp="label" placeholder="Choose word" />
            </Form.Item>
          )}
          <Form.Item name="subantaId" label="Subanta" rules={[{ required: true, message: 'Choose a subanta' }]}>
            <Select
              showSearch
              allowClear
              optionFilterProp="label"
              placeholder={subantaLoading ? 'Loading subantas…' : 'Search by word, IAST, or id…'}
              options={subantaOptions}
              loading={subantaLoading}
              style={{ width: '100%' }}
              popupMatchSelectWidth={false}
              styles={{ popup: { root: { minWidth: 360 } } }}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
